import React, {useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {observer} from 'mobx-react-lite';

import CouponModal from '../../../components/modals/CouponModal';
import {LastMinuteOptionsCarousel} from '../../../components/ProductCarousel';
import TitleLine from '../../../components/rewards/TitleLine';
import SafeAreaPadding from '../../../components/SafeAreaPadding';
import Button from '../../../components/themed/Button';
import ContentView from '../../../components/ui-controls/layout/ContentView';
import {useWebScreenDimensions} from '../../../hooks/useWebScreenDimensions';
import {AppRoute} from '../../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../../models/navigationTypes';
import {useStores} from '../../../stores/use-stores';
import theme from '../../../style/theme';
import {OrderLine} from '../../../transport/apiServiceResponse';
import CartCoupon from '../CartCoupon';
import CartLineItem from '../components/CartLineItem';
import OrderTotals from '../components/OrderTotals';
import PickupDeliveryDetails from '../components/PickupDeliveryDetails';
import {handleContinue, useRedirectEmptyCart} from '../util/validation';

const CheckoutReviewScreen = observer(
  ({
    navigation,
  }: RouteNavigatorScreenProps<AppRoute.CART_ORDER_CHECKOUT_REVIEW>) => {
    // redirect empty cart
    useRedirectEmptyCart(navigation);

    const {cartStore, paymentStore, userStore} = useStores();
    const {isWebDesktopScreen} = useWebScreenDimensions();

    const [isCouponModalVisible, setIsCouponModalVisible] = useState(false);
    return (
      <ContentView testID="checkout-screen">
        <KeyboardAwareScrollView
          style={{backgroundColor: 'white'}}
          keyboardShouldPersistTaps="always"
          showsVerticalScrollIndicator={isWebDesktopScreen}
          showsHorizontalScrollIndicator={isWebDesktopScreen}
          contentContainerStyle={{gap: 16}}>
          <View style={styles.section} testID="checkout-screen-your-order">
            {!isWebDesktopScreen && <TitleLine title="REVIEW YOUR ORDER" />}
            {cartStore.currentOrder?.order_lines
              ?.filter((line: OrderLine) => !line.charge)
              .map((item: OrderLine, index: number) => (
                <CartLineItem
                  item={{
                    ...item,
                    stickers: item?.line?.stickers,
                    extra_prices: item?.line?.extra_prices,
                  }}
                  index={index}
                  key={item.product_id}
                />
              ))}
          </View>
          <PickupDeliveryDetails />
          <CartCoupon onCouponPress={() => setIsCouponModalVisible(true)} />
          <OrderTotals>
            <Button
              title="GO TO PAYMENT"
              onPress={handleContinue({
                paymentStore,
                cartStore,
                userStore,
                callback: () => {
                  navigation.navigate(AppRoute.CART_ORDER_CHECKOUT_PAYMENT);
                },
              })}
              disabled={cartStore.submittingOrder}
              loading={cartStore.submittingOrder}
              testID="checkout-screen-place-order-button"
            />
          </OrderTotals>
          <LastMinuteOptionsCarousel />
        </KeyboardAwareScrollView>
        <CouponModal
          open={isCouponModalVisible}
          callback={() => setIsCouponModalVisible(false)}
        />
        <SafeAreaPadding bottom />
      </ContentView>
    );
  },
);

export default CheckoutReviewScreen;

const styles = StyleSheet.create({
  webDesktopHorizontalContent: {
    flexDirection: 'row',
    gap: 16,
  },
  webDesktopSectionTitle: {
    color: theme.colour.text.heading.primary,
    fontSize: 16,
    fontWeight: 'bold',
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    minHeight: 22,
    backgroundColor: 'white',
  },
  totalText: {
    textAlign: 'right',
    color: theme.colour.text.heading.primary,
    fontWeight: '500',
  },
  totalName: {
    flex: 3,
  },
  totalAmount: {
    flex: 1,
  },
  activityHolder: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  cartItemSpacing: {
    marginBottom: 8,
  },
  bfButton: {
    marginVertical: 16,
  },
  label: {
    color: 'grey',
    fontSize: 12,
    marginBottom: 4,
  },
  section: {
    marginTop: 8,
  },
  confirmText: {
    marginVertical: 8,
    color: theme.colour.text.content.primary,
  },
});
